"use client";

import React, { useEffect, useMemo } from "react";
import Link from "next/link";
import Image from "next/image";
import { FaCalendarAlt, FaFilePdf, FaMapMarkerAlt } from "react-icons/fa";
import getNormalizedCourseName from "@/utils/getNormalizedCourseName";
import { buildClassroomUrl } from "@/utils/buildClassroomUrl";
import { useSearchParams } from "next/navigation";
import { getUtmParams } from "@/utils/getUtmParams";
import { resolveUtmParams, saveUtmParams } from "@/utils/utmStorage";
import ScheduleDisplay, { type Schedule } from "./ScheduleDisplay";

const ROUTES = {
  CAREER: "/career",
  COURSE: "/course",
};

const IMAGE_PATHS = {
  COURSES_DIR: "/images/imagesCourses",
  DEFAULT_CARD: "/images/cardRollingCode.webp",
  STUDY_PLANS_DIR: "/pdfs/study-plans",
};

interface PlanData {
  _id: string;
  name: string;
  promoName?: string;
  promoPercentage?: number;
  fixInstallmentsOffer?: number;
  priceSchema: number[];
}

interface CareerData {
  name?: string;
  description?: string;
  durationMonths?: number;
  downloadablePdf?: boolean;
}

interface CareerItem {
  _id: string;
  course: string;
  classroom: string;
  schedule: Schedule[];
  initialDate: string;
  coverThumbnail: string;
  slackChannel?: string;
  plan: string;
  mode?: string;
  officeLocation?: string;
  careerData?: CareerData;
  planData?: PlanData;
  fixInstallmentsOffer?: number;
  downloadablePdf?: boolean;
}

interface CourseItem {
  _id: string;
  course: string;
  classroom: string;
  schedule: Schedule[];
  initialDate: string;
  coverThumbnail: string;
  slackChannel?: string;
  plan: string;
  mode?: string;
  officeLocation?: string;
  planData?: PlanData;
  fixInstallmentsOffer?: number;
  downloadablePdf?: boolean;
}

interface Translations {
  [key: string]: string;
}

type CardLiveCourseItemType = CareerItem | CourseItem;

interface CardLiveCourseItemsProps {
  type: "career" | "course";
  item: CardLiveCourseItemType;
  translations: Translations;
  formattedDate: string;
  normalizedClassroom?: string;
  checkoutUrl?: string;
  badgeConfig?: unknown;
  onSignUp?: () => void;
  goToSubriptionPage?: boolean;
}

const CardLiveCourseItems: React.FC<CardLiveCourseItemsProps> = ({
  type,
  item,
  translations,
  formattedDate,
  checkoutUrl,
  onSignUp,
  goToSubriptionPage
}) => {
  const searchParams = useSearchParams();

  const currentUtmParams = getUtmParams(searchParams);
  const utmParams = resolveUtmParams(currentUtmParams);
  const utmSource = utmParams.utm_source;
  const utmMedium = utmParams.utm_medium;
  const utmCampaign = utmParams.utm_campaign;
  const utmId = utmParams.utm_id;

  const utmQueryString = useMemo(() => {
    const params = new URLSearchParams();
    if (utmSource) params.set("utm_source", utmSource);
    if (utmMedium) params.set("utm_medium", utmMedium);
    if (utmCampaign) params.set("utm_campaign", utmCampaign);
    if (utmId) params.set("utm_id", utmId);
    return params.toString();
  }, [utmSource, utmMedium, utmCampaign, utmId]);
  const utmSuffix = utmQueryString ? `&${utmQueryString}` : "";

  const checkoutUrlWithUtm = useMemo(() => {
    if (!checkoutUrl) {
      return checkoutUrl;
    }

    if (!utmQueryString) {
      return checkoutUrl;
    }

    try {
      const url = new URL(checkoutUrl);
      const utmQuery = new URLSearchParams(utmQueryString);
      utmQuery.forEach((value, key) => {
        if (!url.searchParams.get(key)) {
          url.searchParams.set(key, value);
        }
      });
      return url.toString();
    } catch {
      const separator = checkoutUrl.includes("?") ? "&" : "?";
      return `${checkoutUrl}${separator}${utmQueryString}`;
    }
  }, [checkoutUrl, utmQueryString]);

  useEffect(() => {
    saveUtmParams(getUtmParams(searchParams));
  }, [searchParams]);

  const classroom = item.classroom;
  const coverThumbnail = item.coverThumbnail;
  const mode = item.mode || (type === "career" ? "ONLINE" : undefined);
  const isPresencialMode = mode?.trim().toLowerCase() === "presencial";
  const officeLocation = item.officeLocation?.trim();
  const schedule = item.schedule || [];
  const planData = item.planData;
  const careerData = type === "career" && 'careerData' in item ? item.careerData : undefined;
  const normalizeSlug = getNormalizedCourseName('', careerData?.name || item.course);
  return (
    <div className="relative border p-6 shadow-md w-full sm:w-[80%] md:w-[45%] lg:w-[35%] xl:w-[30%] flex flex-col justify-between h-100">
      {mode && (
        <div className="absolute top-2 right-2 flex flex-col items-end gap-1">
          <div className="px-4 text-sm font-semibold text-white bg-red-600">
            {mode}
          </div>
          {isPresencialMode && officeLocation && (
            <div className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white/95 px-2 py-0.5 text-[10px] text-gray-600">
              <FaMapMarkerAlt className="text-[10px] text-gray-500" />
              <span className="font-medium">{officeLocation}</span>
            </div>
          )}
        </div>
      )}
      <Image
        src={
          coverThumbnail
            ? `${IMAGE_PATHS.COURSES_DIR}/${coverThumbnail}.webp`
            : IMAGE_PATHS.DEFAULT_CARD
        }
        alt={item.course}
        className={`mt-3 h-40 w-full rounded-lg text-gray-400 ${
          coverThumbnail ? "object-cover object-center" : "bg-gray-950 p-5 py-8 object-contain"
        }`}
        width={1200}
        height={675}
        priority
      />
  {careerData ? (
        <Link
          href={goToSubriptionPage && checkoutUrlWithUtm ? checkoutUrlWithUtm : buildClassroomUrl(ROUTES.CAREER, normalizeSlug, classroom, utmSuffix)}
        >
          <h2 className="py-3 text-lg font-semibold leading-snug text-red-600 hover:text-red-800 sm:text-xl">
            {careerData.description
              ? `${careerData.description  || item.course}`
              : careerData.name || item.course}
          </h2>
        </Link>
      ) : (
         <Link
          href={goToSubriptionPage && checkoutUrlWithUtm ? checkoutUrlWithUtm :  buildClassroomUrl(ROUTES.COURSE, normalizeSlug, classroom, utmSuffix)}
        >
          <h2 className="py-2 text-lg font-semibold leading-snug text-red-600 hover:text-red-800 sm:text-xl">
            {item.course}
          </h2>
        </Link>
      )}
      <div className="py-2 border-t-2 border-b-2 border-gray-300">
  {careerData?.durationMonths && (
          <div className="flex items-center justify-between my-2 mb-4">
            <p className="flex items-center text-gray-600">
              <FaCalendarAlt className="mr-2" />{" "}
              <span className="font-bold text-black">
                {careerData?.durationMonths} meses
              </span>
            </p>
            {(item.downloadablePdf || careerData?.downloadablePdf) && (
              <a
                href={`${IMAGE_PATHS.STUDY_PLANS_DIR}/${normalizeSlug}.pdf`}
                download
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-black hover:text-gray-700 transition-colors text-sm"
              >
                <FaFilePdf className="text-base" />
                <span>Descargar programa</span>
              </a>
            )}
          </div>
        )}
        {!careerData && item.downloadablePdf && (
          <div className="flex justify-end mb-4">
            <a
              href={`${IMAGE_PATHS.STUDY_PLANS_DIR}/${normalizeSlug}.pdf`}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-black hover:text-gray-700 transition-colors text-sm"
            >
              <FaFilePdf className="text-base" />
              <span>Descargar programa</span>
            </a>
          </div>
        )}
        <div className="grid grid-cols-1 gap-2 my-4 sm:grid-cols-2">
          <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
            <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
              {translations.chooseCareerCommission || "Comisión"}
            </span>
            <p className="text-base font-semibold text-red-600">{classroom}</p>
          </div>
          <div className="rounded-md border border-red-100 bg-red-50 px-3 py-2">
            <span className="text-xs font-medium tracking-wide text-red-600 uppercase">
              {translations.chooseCareerStartDate || "Inicio"}
            </span>
            <p className="text-base font-bold text-red-700">{formattedDate}</p>
          </div>
        </div>
        <ScheduleDisplay schedule={schedule} translations={translations} />
      </div>
          {planData && planData.promoName && (
                <span className="py-2 font-bold text-red-500">
                  PROMO {planData.promoName}
                </span>
              )}
  {planData && planData.priceSchema?.[0] && (
        <p className="flex flex-col mt-4 mb-2 text-xs font-bold text-gray-500">
          {planData.fixInstallmentsOffer ? (
            <>
              {translations.chooseCareerPrice || "DESDE"}{" "}
              <span className="text-3xl font-bold text-red-600">
                {planData && planData.priceSchema && (
                  <>
                    ${Math.floor(planData.priceSchema[0] / planData.fixInstallmentsOffer)}{" "}
                    <span className="font-light">ARS</span>
                    <span className="block text-sm font-bold text-green-600">
                      {planData.fixInstallmentsOffer} CUOTAS SIN INTERES
                    </span>
                  </>
                )}
              </span>
            </>
          ) : (
            <>
          
              {translations.chooseCareerPrice || "DESDE"}
              <span className="text-3xl font-bold text-red-600">
                {planData && planData.promoPercentage && ! planData?.fixInstallmentsOffer && (
                  <span className="pr-2 text-lg font-normal text-gray-500 line-through">
                    {(
                      Math.floor(
                        (planData.priceSchema[0] /
                          ((100 - planData.promoPercentage) * 0.01)) *
                          100
                      ) / 100
                    )}
                  </span>
                )}
                {planData && planData.priceSchema && (
                  <>
                    ${planData.priceSchema[0]}{" "}
                    <span className="font-light">ARS</span>
                  </>
                )}
              </span>
            </>
          )}
        </p>
      )}
      {checkoutUrlWithUtm && (
        <Link href={checkoutUrlWithUtm}>
          <button
            className="w-full py-2 mt-3 font-bold text-white bg-red-600 hover:bg-red-700"
            onClick={onSignUp}
          > 
            {translations.chooseCareerSignUp || "INSCRIBIRME AHORA"}
          </button>
        </Link>
      )}
    </div>
  );
};

export default CardLiveCourseItems;